import {
  Box,
  Button,
  capitalize,
  Card,
  CardContent,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Formik } from 'formik';
import { type FC, useEffect, useState } from 'react';
import * as Yup from 'yup';

import useAuth from 'src/hooks/useAuth';
import useMounted from 'src/hooks/useMounted';

import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { isEmpty, isUndefined } from 'lodash';
import { useTranslation } from 'react-i18next';
import { legalDocumentApi } from 'src/api/legalDocumentApi';
import { userApi } from 'src/api/userApi';
import LegalDocumentModal from 'src/components/dashboard/LegalDocumentModal';
import logger from 'src/utils/logger';

const useStyles = makeStyles((theme) => ({
  docLink: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  lockIcon: {
    background: theme.palette.primary.main,
    width: 55,
    height: 55,
    padding: 14,
    borderRadius: 4,
    position: 'relative',
    top: '-25px',
    left: 49,
  },
  label: {
    color: 'rgba(84, 110, 122, 1)',
    '& a': {
      textDecoration: 'underline',
      color: theme.palette.primary.main,
    },
  },
  checkboxLabel: {
    paddingLeft: '31px',
  },
  emptyCheckboxSpace: {
    '& .MuiIconButton-label, & .MuiSvgIcon-root': {
      display: 'none',
      pointerEvents: 'none'
    },
    width: 42,
    height: 42,
    display: 'none',
    paddingLeft: '42px',
    pointerEvents: 'none'
  },
  card: {
    borderRadius: 8,
    overflow: 'unset',
  },
  loginTermsOfUseInfo: {
    background: 'rgba(246, 246, 246, 1)',
    color: 'rgba(138, 142, 144, 1)',
    borderRadius: '4px',
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    '& svg': {
      fill: 'rgba(138, 142, 144, 1)',
    },
  },
  loginTermsOfUseInfoTitle: {
    color: 'rgba(138, 142, 144, 1)',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 48px 64px !important',
    [theme.breakpoints.down('sm')]: {
      padding: '0 16px 32px !important',
    }
  },
  textField: {
    borderRadius: 4,
  },
  submitButton: {
    borderRadius: 4,
    textTransform: 'uppercase',
    [theme.breakpoints.down('md')]: {
      marginBottom: 15,
      width: '100%'
    }
  },
  forgotPassword: {
    color: theme.palette.warning.main,
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    }
  },
  error: {
    borderRadius: 4,
  },
}));

const LoginJWT: FC = (props) => {
  const classes = useStyles();
  const mounted = useMounted();
  const { login } = useAuth() as any;
  const locale = localStorage.getItem('locale');
  const { t } = useTranslation(locale);
  const [isPrivacyPolicyDocReviewedDB, setIsPrivacyPolicyDocReviewedDB] = useState<boolean>(false);
  const [isTermsAndConditionsDocReviewedDB, setIsTermsAndConditionsDocReviewedDB] = useState<boolean>(false);
  const [isPrivacyPolicyDocReviewed, setIsPrivacyPolicyDocReviewed] = useState<boolean>(false);
  const [isTermsAndConditionsDocReviewed, setIsTermsAndConditionsDocReviewed] = useState<boolean>(false);
  const [defaultLegalDocs, setDefaultLegalDocs] = useState<any>([]);

  const [isEmailAutoFilled, setIsEmailAutoFilled] = useState<boolean>(false);
  const [openLegalDocumentModal, setOpenModalLegalDocument] = useState<boolean>(false);
  const [emailValue, setEmail] = useState<string>('');
  const [emailExistsInDb, setIEmailExistsInDb] = useState<boolean>(false);
  const [selectedLegalDocument, setSelectedLegalDocument] = useState<any>({});

  // Create a validation schema
  const emailSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required')
  });

  const isValidEmail = async (email) => {
    try {
      await emailSchema.validate({
        email
      });
      return true;
    } catch (err) {
      return false;
    }
  };

  const handlePopUp = (e: any, legalDocument: any) => {
    if (e.target?.toString()?.indexOf('HTMLSpanElement') !== -1 && e.target.getAttribute('data-terms-of-use')?.toString() === 'true') {
      setOpenModalLegalDocument(true);
    }

    if (e.target?.toString()?.indexOf('HTMLSpanElement') !== -1 && e.target.getAttribute('data-privacy-policy')?.toString() === 'true') {
      setOpenModalLegalDocument(true);
    }

    setSelectedLegalDocument(legalDocument);
  };

  const handleCheckboxChange = (e: any) => {
    if (e.target.name === 'terms_of_use') {
      setIsTermsAndConditionsDocReviewed(e.target.checked);
      setIsPrivacyPolicyDocReviewed(e.target.checked);
    } else {
      setIsPrivacyPolicyDocReviewed(e.target.checked);
    }
  };

  const closeLegalDocumentModal = () => {
    setOpenModalLegalDocument(false);
  };

  const getUserByEmail = async (e: any) => {
    try {
      // console.log('12222dasdasdasdasd', e);
      const email = e === 'string' ? e : e.target.value?.trim();
      if (email?.length > 0) {
        let res: any = await userApi?.getUserByEmail(email);
        res = !isUndefined(res?.isCookieConsent)
          && !isUndefined(res?.isPrivacyPolicyDocReviewed)
          && !isUndefined(res?.isTermsAndConditionsDocReviewed)
          && !isUndefined(res?.user_id) ? res : {
            isCookieConsent: 0,
            isPrivacyPolicyDocReviewed: 0,
            isTermsAndConditionsDocReviewed: 0,
            user_id: 0,
          };

        if (!isEmpty(res)) {
          setIEmailExistsInDb(true);
          setIsPrivacyPolicyDocReviewed(res?.isPrivacyPolicyDocReviewed);
          setIsTermsAndConditionsDocReviewed(res?.isTermsAndConditionsDocReviewed);
          setIsPrivacyPolicyDocReviewedDB(res?.isPrivacyPolicyDocReviewed);
          setIsTermsAndConditionsDocReviewedDB(res?.isTermsAndConditionsDocReviewed);
        }
      } else {
        setIEmailExistsInDb(false);
      }
      // eslint-disable-next-line no-empty
    } catch (err) {
      setIEmailExistsInDb(false);
      logger('error', err);
    }
  };

  const getDefaultLegalDocument = async () => {
    try {
      const res: any = await legalDocumentApi?.getDefaultLegalDocument();
      // res = res?.length > 0 ? res[0] : res;
      setDefaultLegalDocs(res);
      // eslint-disable-next-line no-empty
    } catch (err) {
      logger('error', err);
      setDefaultLegalDocs([]);
    }
  };

  const getCheckboxLabel = (defaultLegalDoc: any) => `<div>${defaultLegalDoc?.checkbox_label[locale || 'en']?.replace('[link]',
    `<span
          data-${defaultLegalDoc?.legalDocType?.toLowerCase()?.replaceAll(' ', '-')}="true"
          class="${classes.docLink}"
        >${capitalize(defaultLegalDoc?.name[locale || 'en'])}
      </span>`)}</div>`;

  useEffect(() => {
    if ((!isPrivacyPolicyDocReviewedDB || !isTermsAndConditionsDocReviewedDB) && isValidEmail(emailValue)) {
      getDefaultLegalDocument();
    }
  }, [isPrivacyPolicyDocReviewedDB, isTermsAndConditionsDocReviewedDB, emailValue]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        justifyContent: 'center'
      }}
    >
      <Container
        maxWidth="sm"
        sx={{ p: '0 !important' }}
      >
        <Card className={classes.card}>
          <svg
            className={classes.lockIcon}
            width="20"
            height="22"
            viewBox="0 0 20 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M16.5625 9.125H15.625V6.3125C15.625 3.21875 13.0938 0.6875 10 0.6875C6.90625 0.6875 4.375 3.21875 4.375 6.3125V9.125H3.4375C1.84375 9.125 0.625 10.3438 0.625 11.9375V18.5C0.625 20.0938 1.84375 21.3125 3.4375 21.3125H16.5625C18.1562 21.3125 19.375 20.0938 19.375 18.5V11.9375C19.375 10.3438 18.1562 9.125 16.5625 9.125ZM6.25 6.3125C6.25 4.25 7.9375 2.5625 10 2.5625C12.0625 2.5625 13.75 4.25 13.75 6.3125V9.125H6.25V6.3125ZM16.5625 19.4375C17.125 19.4375 17.5 19.0625 17.5 18.5V11.9375C17.5 11.375 17.125 11 16.5625 11H3.4375C2.875 11 2.5 11.375 2.5 11.9375V18.5C2.5 19.0625 2.875 19.4375 3.4375 19.4375H16.5625Z"
              fill="white"
            />
          </svg>
          <CardContent
            className={classes.cardContent}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                mb: 2
              }}
            >
              <div>
                <Typography
                  color="textPrimary"
                  variant="h4"
                >
                  {t('login_sign_in')}
                </Typography>
              </div>
            </Box>
            <Grid
              item
              xs={12}
            >
              <Divider />
            </Grid>
            <Box
              sx={{
                flexGrow: 1,
                mt: 2
              }}
            >
              <Formik
                initialValues={{
                  email: emailValue,
                  password: '',
                  autofilled: isEmailAutoFilled,
                  terms_of_use: isTermsAndConditionsDocReviewed || false,
                  submit: null
                }}
                validationSchema={
                  Yup
                    .object()
                    .shape({
                      email: Yup
                        .string()
                        // .email('Must be a valid email')
                        .max(255)
                        .required('Email is required'),
                      password: Yup
                        .string()
                        .max(255)
                        .required('Password is required')
                    })
                }
                onSubmit={async (values, {
                  setErrors,
                  setStatus,
                  setSubmitting
                }): Promise<void> => {
                  try {
                    await login(emailValue, values.password, isTermsAndConditionsDocReviewed);
                    if (mounted.current) {
                      setStatus({ success: true });
                      setSubmitting(false);
                    }
                  } catch (err) {
                    localStorage.clear();
                    localStorage.setItem('locale', locale);
                    if (mounted.current) {
                      setStatus({ success: false });
                      setErrors({ submit: err?.response?.status === 403 ? t('login_permission_error') : err.message });
                      setSubmitting(false);
                    }
                  }
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                  touched,
                  values
                }): JSX.Element => (
                  <form
                    noValidate
                    onSubmit={handleSubmit}
                    {...props}
                    id="login_form"
                  >
                    <TextField
                      InputProps={{
                        classes: {
                          root: classes.textField
                        },
                        onAnimationStart: (event) => {
                          if (event.animationName === 'mui-auto-fill') {
                            setIsEmailAutoFilled(true);
                          }
                        },
                      }}
                      autoFocus
                      error={Boolean(touched.email && errors.email)}
                      fullWidth
                      helperText={touched.email && errors.email}
                      label={t('login_email_address')}
                      margin="normal"
                      id="email"
                      name="email"
                      onBlur={getUserByEmail}
                      onChange={(event) => {
                        const email = event?.target?.value?.trim();
                        if (email?.length === 0) {
                          setDefaultLegalDocs([]);
                        }
                        if (!isValidEmail(email)) {
                          setIEmailExistsInDb(true);
                        }
                        setFieldValue('email', email);
                        setEmail(email);
                      }}
                      type="email"
                      value={emailValue}
                      variant="outlined"
                      autoComplete="off"
                    />
                    <TextField
                      InputProps={{
                        classes: {
                          root: classes.textField
                        },
                      }}
                      error={Boolean(touched.password && errors.password)}
                      fullWidth
                      helperText={touched.password && errors.password}
                      label={t('login_form_password_label')}
                      margin="normal"
                      id="password"
                      name="password"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="password"
                      value={values.password}
                      variant="outlined"
                    />
                    {((!isPrivacyPolicyDocReviewedDB || !isTermsAndConditionsDocReviewedDB) && emailExistsInDb && defaultLegalDocs?.length > 0) && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                      >
                        <Box mt={2}>
                          <Alert
                            severity="warning"
                            className={classes.loginTermsOfUseInfo}
                          >
                            <Typography
                              color="textPrimary"
                              variant="subtitle2"
                              className={classes.loginTermsOfUseInfoTitle}
                            >
                              {t('loginTermsOfUseInfo')}
                            </Typography>
                          </Alert>
                        </Box>
                        <FormGroup>
                          {defaultLegalDocs?.map((defaultLegalDoc) => {
                            const html = getCheckboxLabel(defaultLegalDoc);
                            return (defaultLegalDoc?.isMandatory ? (
                              <FormControlLabel
                                control={(
                                  <Checkbox
                                    className={!defaultLegalDoc?.isMandatory ? classes.emptyCheckboxSpace : ''}
                                    name={defaultLegalDoc?.legalDocType?.toLowerCase()?.replaceAll(' ', '_')}
                                    onChange={(e) => handleCheckboxChange(e)}
                                  />
                                )}
                                label={(
                                  <Typography
                                    color="textPrimary"
                                    variant="subtitle2"
                                    onClick={(e) => handlePopUp(e, defaultLegalDoc)}
                                    className={clsx(classes.label, {
                                      [classes.checkboxLabel]: !defaultLegalDoc?.isMandatory
                                    })}
                                    dangerouslySetInnerHTML={{
                                      __html: html
                                    }}
                                  />
                                )}
                              />
                            ) : (
                              <Typography
                                color="textPrimary"
                                variant="subtitle2"
                                className={clsx(classes.label, {
                                  [classes.checkboxLabel]: !defaultLegalDoc?.isMandatory
                                })}
                                onClick={(e) => handlePopUp(e, defaultLegalDoc)}
                                dangerouslySetInnerHTML={{
                                  __html:
                                    defaultLegalDoc?.checkbox_label[locale || 'en']?.replace('[link]', `<span data-${defaultLegalDoc?.legalDocType?.toLowerCase()?.replaceAll(' ', '-')}="true" class="${classes.docLink}">${capitalize(!isUndefined(defaultLegalDoc?.name[locale]) ? defaultLegalDoc?.name[locale] : defaultLegalDoc?.name?.en)}</span>`)
                                }}
                              />
                            )
                            );
                          })}
                        </FormGroup>
                      </Grid>
                    )}
                    {errors.submit && (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                      >
                        <Box mt={2}>
                          <Alert
                            severity="error"
                            className={classes.error}
                          >
                            <div>
                              <strong>
                                {errors.submit}
                              </strong>
                            </div>
                          </Alert>
                        </Box>
                      </Grid>
                    )}
                    <Box
                      sx={{ mt: 2 }}
                      className={classes.buttonWrapper}
                    >
                      <Grid
                        item
                        xs={12}
                        md={4}
                      >
                        <Button
                          className={classes.submitButton}
                          color="primary"
                          disabled={isSubmitting || !isPrivacyPolicyDocReviewed || !isTermsAndConditionsDocReviewed || !emailExistsInDb}
                          fullWidth
                          size="large"
                          type="submit"
                          variant="contained"
                          id="login_button"
                        >
                          {t('login_sign_in')}
                        </Button>
                      </Grid>
                      <Grid
                        xs={12}
                        md={4}
                        item
                      >
                        <a
                          className={classes.forgotPassword}
                          href="/forgot-password"
                        >
                          {t('login_forget_password')}
                        </a>
                      </Grid>
                    </Box>
                  </form>
                )}
              </Formik>
            </Box>
          </CardContent>
        </Card>
      </Container>
      {openLegalDocumentModal && (
        <LegalDocumentModal
          openLegalDocumentModal={openLegalDocumentModal}
          closeLegalDocumentModal={closeLegalDocumentModal}
          selectedLegalDocument={selectedLegalDocument}
        />
      )}
    </Box>
  );
};

export default LoginJWT;
